import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Modal, Form, Input, message, Space, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import axios from 'axios';
import { API_BASE_URL } from '../constants/api';
import SearchBar from './common/SearchBar';
import { useDebounce } from 'use-debounce';

const RepositoryManagement = () => {
  const [repositories, setRepositories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingRepository, setEditingRepository] = useState(null);
  const [form] = Form.useForm();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [debouncedSearchTerm] = useDebounce(searchTerm, 300);

  useEffect(() => {
    fetchRepositories();
  }, []);

  useEffect(() => {
    if (debouncedSearchTerm) {
      fetchRepositories(debouncedSearchTerm);
    } else {
      fetchRepositories();
    }
  }, [debouncedSearchTerm]);

  const fetchRepositories = async (query = '') => {
    setSearchLoading(true);
    try {
      const params = query ? { Name: query } : {};
      const response = await axios.get(`${API_BASE_URL}Repository/List`, { params });
      if (response.data.code === 0) {
        setRepositories(response.data.data || []);
      } else {
        message.error(response.data.message || 'فشل في جلب المستودعات');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'خطأ في جلب المستودعات';
      message.error(errorMessage);
    }
    setSearchLoading(false);
    setLoading(false);
  };

  const handleCreate = () => {
    setEditingRepository(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingRepository(record);
    form.setFieldsValue(record);
    setIsModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      const response = await axios.delete(`${API_BASE_URL}Repository/Delete/${id}`);
      if (response.data.code === 0) {
        message.success(response.data.message || 'تم حذف المستودع بنجاح');
        fetchRepositories();
      } else {
        message.error(response.data.message || 'فشل في حذف المستودع');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'خطأ في حذف المستودع';
      message.error(errorMessage);
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      if (editingRepository && editingRepository.id) {
        const response = await axios.put(`${API_BASE_URL}Repository/Update/${editingRepository.id}`, values);
        if (response.data.code === 0) {
          message.success('تم تحديث المستودع بنجاح');
          setIsModalVisible(false);
          fetchRepositories();
        } else {
          message.error(response.data.message || 'فشل في تحديث المستودع');
        }
      } else {
        const response = await axios.post(`${API_BASE_URL}Repository/Create`, values);
        if (response.data.code === 0) {
          message.success('تم إنشاء المستودع بنجاح');
          setIsModalVisible(false);
          fetchRepositories();
        } else {
          message.error(response.data.message || 'فشل في إنشاء المستودع');
        }
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'خطأ في حفظ المستودع';
      message.error(errorMessage);
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
  };

  const columns = [
    {
      title: 'معرف',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'الاسم',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'إجراءات',
      key: 'actions',
      render: (text, record) => (
        <Space size="middle">
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Popconfirm
            title="هل أنت متأكد من حذف هذا المستودع؟"
            onConfirm={() => handleDelete(record.id)}
            okText="نعم"
            cancelText="لا"
          >
            <Button type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          إضافة مستودع
        </Button>
        <SearchBar
          onSearch={setSearchTerm}
          loading={searchLoading}
          placeholder="ابحث بالاسم..."
        />
      </div>
      <div className="table-responsive-wrapper">
        <Table
          columns={columns}
          dataSource={repositories}
          rowKey="id"
          loading={loading || searchLoading}
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: searchTerm ? 'لا توجد مستودعات بهذا الاسم' : 'لا توجد مستودعات' }}
        />
      </div>
      <Modal
        title={editingRepository ? 'تعديل المستودع' : 'إضافة مستودع'}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        width="90%"
        style={{ maxWidth: 600 }}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="الاسم"
            rules={[{ required: true, message: 'يرجى إدخال اسم المستودع!' }]}
          >
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default RepositoryManagement;
